import { ReactNode, useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Building2, Tag, FolderOpen, Settings, Menu, X, ExternalLink, LogOut } from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';

interface AdminLayoutProps {
  children?: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('ave_admin_auth') === 'true';
    if (!isAuthenticated) {
      navigate(ROUTE_PATHS.ADMIN_LOGIN);
    }
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem('ave_admin_auth');
    navigate(ROUTE_PATHS.ADMIN);
  };

  const navItems = [
    { path: ROUTE_PATHS.ADMIN_DASHBOARD, label: 'Dashboard', icon: LayoutDashboard },
    { path: ROUTE_PATHS.ADMIN_PROPERTIES, label: 'Properties', icon: Building2 },
    { path: ROUTE_PATHS.ADMIN_CATEGORIES, label: 'Categories', icon: Tag },
    { path: ROUTE_PATHS.ADMIN_FILES, label: 'Files', icon: FolderOpen },
    { path: ROUTE_PATHS.ADMIN_SETTINGS, label: 'Settings', icon: Settings },
  ];

  const getPageTitle = () => {
    const currentPath = location.pathname;
    if (currentPath === ROUTE_PATHS.ADMIN_DASHBOARD) return 'Dashboard';
    if (currentPath === ROUTE_PATHS.ADMIN_PROPERTIES) return 'Properties';
    if (currentPath === ROUTE_PATHS.ADMIN_PROPERTIES_NEW) return 'Add Property';
    if (currentPath.includes('/edit')) return 'Edit Property';
    if (currentPath === ROUTE_PATHS.ADMIN_CATEGORIES) return 'Categories';
    if (currentPath === ROUTE_PATHS.ADMIN_FILES) return 'Files';
    if (currentPath === ROUTE_PATHS.ADMIN_SETTINGS) return 'Settings';
    return 'Admin Panel';
  };

  return (
    <div className="min-h-screen bg-background">
      <aside
        className={`fixed top-0 left-0 z-40 h-screen w-64 bg-primary transition-transform duration-300 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-6">
            <h1 className="font-serif text-xl font-semibold text-accent">AVE Admin</h1>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="text-sidebar-foreground lg:hidden"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="flex-1 space-y-1 px-3 py-4">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-sidebar-accent text-accent'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-accent'
                    }`
                  }
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </aside>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-border bg-card px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="text-foreground lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>
            <h2 className="text-lg font-semibold text-foreground">{getPageTitle()}</h2>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              asChild
              className="gap-2"
            >
              <a href="/" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" />
                View Site
              </a>
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleLogout}
              className="gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </header>

        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
